<template>
  <div v-if="getChartsTemplate?.table">
    <div class="query-dropdown">
      <RexSearchSelect
        width="15rem"
        :data="dropdownValues"
        :current="currentQueryTimeThreshold"
        :isSearch="false"
        :heading="$t('queryDbTimeThresholdDropdown')"
        @onChoose="onQueryTimeThresholdChange"
        :objectValueIndex="1"
        id="query-threshold-select"
      />
      <DatabaseQueryDetailsPdf
        fileName="mongoDB-query-details"
        :headers="headers"
        :queryTimeThreshold="currentQueryTimeThreshold"
        :disabled="isQueryDetailsEmpty"
        :sortCriteria="getSortCriteria"
      />
    </div>
    <div>
      <RexTableCompV2
        :headers="headers"
        :items="queryItemsApp"
        height="37rem"
        :striped="false"
        @fetchNextPageData="
          () => {
            if (allowInfiniteScroll) {
              currentPage++;
              getQueryPerformanceTableData();
            }
          }
        "
        :enableInfiniteScroll="true"
        :enableScrollFooter="dispScrollFooter"
        :loading="loading"
      >
        <template
          v-for="header in headers"
          #[`body-${header.key}`]="{ item }"
          :key="`${header.key}-${getRandomUniqueId()}`"
        >
          <div v-if="header.key === 'timestamp'">
            {{
              $filters.dateformat(
                parseInt(item[header.key]),
                timezoneoffset,
                'DD.MM.YY [at] hh:mm A',
              )
            }}
          </div>
        </template>
        <template
          v-for="header in headers"
          #[`header-${header.key}`]
          :key="header.key"
        >
          <div>
            <RexTableSortField
              v-if="header.sortable"
              :name="header.name"
              @toggleSort="() => toggleSorting(header.key)"
              :isAsc="isAsc"
              :isActiveField="sortingField === header.key"
            />
            <div v-else>
              {{ header.name }}
            </div>
          </div>
        </template>
        <template v-if="dispInfiniteFooterMsg" #infinite-footer>
          <div class="infi-footer">
            <div v-if="isScrollErr">
              {{ $t('dbm.errorWhileFetchingData') }}
              <button class="refresh-btn">
                <BaseIcon
                  name="refresh"
                  @click="
                    resetTableData();
                    getQueryPerformanceTableData();
                  "
                  height="0.8em"
                  width="0.8em"
                  class="refresh-icon"
                />
              </button>
            </div>
            <div v-else>
              {{ $t('dbm.availableDataLoadedCompletely') }}
            </div>
          </div>
        </template>
      </RexTableCompV2>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import {
  mongoQueryTableHeader,
  QueryThresholdValues,
} from '@/utils/templates/databaseTemplate/MongoDB/MongoDb.query.Table.config';
import { getRandomUniqueId } from '@/utils/helper-function';
import {
  SUB_TYPES,
  ASC,
  DES,
} from '@/utils/templates/databaseTemplate/database.constants';
import * as types from '@/components/Database/database.types';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import DatabaseQueryDetailsPdf from '@/components/Database/DatabaseContent/DatabaseQueryDetailsPdf.vue';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
export default {
  components: {
    RexTableCompV2,
    RexSearchSelect,
    DatabaseQueryDetailsPdf,
    RexTableSortField,
  },
  data() {
    return {
      loading: false,
      queryItemsApp: [],
      dropdownValues: QueryThresholdValues,
      currentQueryTimeThreshold: {},
      currentPage: 1,
      pageSize: 20,
      sortingField: types.timeStamp,
      isAsc: false,
      isScrollErr: false,
    };
  },
  created() {
    this.currentQueryTimeThreshold = this.dropdownValues[2];
    this.getQueryPerformanceTableData();
  },
  computed: {
    ...mapState('globalState/app', ['chartsTemplate']),
    ...mapState('databaseState/databaseCommonState', [
      'currentDatabaseType',
      'currentDatabaseService',
    ]),
    ...mapState('databaseState/detail', [
      'currentDatabaseInstance',
      'currentSubType',
    ]),
    ...mapGetters('databaseState/databaseCommonState', ['getCurrentDatabase']),
    ...mapGetters('globalState/settings', ['getDurationTime', 'intervalTime']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    getChartsTemplate() {
      return this.chartsTemplate[this.currentDatabaseType.key][
        this.currentSubType.key
      ];
    },
    dispScrollFooter() {
      return this.queryItemsApp?.length >= 15;
    },
    allowInfiniteScroll() {
      return this.queryItemsApp?.length < this.total;
    },
    dispInfiniteFooterMsg() {
      return !this.allowInfiniteScroll && this.dispScrollFooter;
    },
    isQueryDetailsEmpty() {
      return this.queryItemsApp?.length === 0;
    },
    headers() {
      return mongoQueryTableHeader();
    },
    getSortCriteria() {
      return [
        {
          field: types.queryTableSortFieldMapping[this.sortingField],
          order: this.isAsc ? ASC : DES,
        },
      ];
    },
  },
  methods: {
    ...mapActions('databaseState/databaseCommonState', [
      'GET_ALL_QUERY_RECORDS',
    ]),
    getRandomUniqueId,
    formatTableResp(list) {
      return list.map((item) => {
        return { _id: `${getRandomUniqueId()}`, ...item };
      });
    },
    toggleSorting(sortingField) {
      this.isAsc = !this.isAsc;
      this.sortingField = sortingField;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
    resetTableData() {
      this.queryItemsApp = [];
      this.loading = true;
      this.total = 0;
      this.currentPage = 1;
    },
    async getQueryPerformanceTableData() {
      this.loading = true;
      this.isScrollErr = false;
      try {
        if (
          this.currentDatabaseInstance.key &&
          this.currentSubType.key &&
          this.currentSubType.key === SUB_TYPES.MONGO_QUERYPERFORMANCE
        ) {
          const params = {
            condition: {
              name: types.metricTypes.DBM_QUERIES,
              parentService: this.currentDatabaseInstance.key,
              scope: 'ServiceInstance',
              normal: false,
              searchKeyword: '*',
              queryTimeThreshold: this.currentQueryTimeThreshold.key,
            },
            duration: this.getDurationTime,
            pagination: {
              pageNum: this.currentPage,
              pageSize: this.pageSize,
              needTotal: true,
            },
            sortCriteria: this.getSortCriteria,
          };
          const queryData = await this.GET_ALL_QUERY_RECORDS(params);
          this.total = queryData.total;
          this.queryItemsApp.push(
            ...this.formatTableResp(queryData?.records ?? []),
          );
        } else {
          this.queryItemsApp = [];
          this.total = 0;
        }
        this.loading = false;
      } catch (error) {
        this.queryItemsApp = [];
        this.isScrollErr = true;
        this.loading = false;
      }
    },
    onQueryTimeThresholdChange(data) {
      this.currentQueryTimeThreshold = data;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
  },
  watch: {
    async getDurationTime() {
      this.isAsc = false;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
    'currentDatabaseInstance.key': function () {
      this.isAsc = false;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
    'currentSubType.key': function () {
      this.isAsc = false;
      this.resetTableData();
      this.getQueryPerformanceTableData();
    },
  },
};
</script>
<style lang="scss" scoped>
.query-dropdown {
  @include flex-center-justify(space-between);
  margin: 0.5rem;
  padding-bottom: 1rem;
}
.infi-footer {
  @include flex-center;
  color: $font-tertiary;
  padding: 1.25rem 0rem;
}
</style>
